import { emptyDisplayValue } from 'constants/common';
import { format } from 'date-fns';
import {
  CompanySettingChangeOrderField,
  CompanySettingChangeSource,
  CompanySettingChangesQuery,
} from 'generated/graphql';
import { ReactNode } from 'react';
import { Tooltip, TSelectOption } from '@spotted-zebra-uk/ui-components';
import styles from './AuditLogTab.module.scss';

export const DEFAULT_AUDIT_PAGE_SIZE = 20;

// Sortable header keys are the server's order-field enum values so a
// header click maps straight onto the query's order argument
// (the same pattern as the Roles table).
export const tableHeaders = [
  { key: CompanySettingChangeOrderField.OccurredAt, header: 'Date' },
  { key: CompanySettingChangeOrderField.ChangedBy, header: 'User' },
  { key: CompanySettingChangeOrderField.Setting, header: 'Setting' },
  { key: 'oldValue', header: 'From' },
  { key: 'newValue', header: 'To' },
];

// Setting is unsortable on purpose: rows store the setting's code name
// (`progressionEnabled`) while the column shows its label ("Rejection
// write-back"), so sorting on the stored value looks arbitrary on screen.
export const unsortableHeaderKeys = [
  CompanySettingChangeOrderField.Setting,
  'oldValue',
  'newValue',
];

export const sourceFilterOptions: TSelectOption[] = [
  { id: 'all', label: 'All sources', value: '' },
  {
    id: 'company',
    label: 'Company settings',
    value: CompanySettingChangeSource.Company,
  },
  {
    id: 'ats',
    label: 'Integration settings',
    value: CompanySettingChangeSource.AtsIntegration,
  },
];

/**
 * Human labels for the canonical setting keys the backend records. Any key
 * not listed (a future setting) falls back to the raw key, so new settings
 * are never invisible here.
 */
const SETTING_LABELS: Record<string, string> = {
  // Company pipe (monolith `updateCompany`)
  name: 'Company name',
  companyLifecycle: 'Company lifecycle',
  csmUserId: 'CSM user',
  expiresAt: 'Company expiry date',
  allowedEmailDomains: 'Allowed email domains',
  areCognitiveTestsBlocked: 'Block cognitive tests',
  areRffSoftSkillsDisabled: 'Disable RFF soft skills',
  isViewGuidanceEnabled: 'View guidance',
  isInterviewGuideScoresEnabled: 'Interview guide scores',
  isInterviewGuideConfirmationEnabled: 'Interview guide confirmation',
  isLegalAcknowledgementsEnabled: 'Legal acknowledgements',
  isSkillsEvaluationScoreEnabled: 'Skills evaluation score',
  isRoleRequirementsEnabled: 'Role requirements',
  aiPrivateNetworking: 'AI private networking',
  rfuModelType: 'RFU model type',
  reasonableAdjustmentsOption: 'Reasonable adjustments',
  assessmentExpiryOption: 'Assessment expiry',
  managerReportId: 'Manager report',
  candidateReportId: 'Candidate report',
  hideReports: 'Hide reports',
  atsPullJobsFromDate: 'ATS pull jobs from date',
  generateManagerReport: 'Generate manager report',
  generateCandidateReport: 'Generate candidate report',
  emailCandidateReport: 'Email candidate report',
  projectTeamEmailSettings: 'Team email settings',
  enableCompanyCodingAssessments: 'Coding assessments',
  areMultiAssessmentStagesEnabled: 'Multi-assessment stages',
  isPersonalityTestMandatory: 'Personality test mandatory',
  isAtsRoleCreationWithRequisitionEnabled: 'ATS role creation with requisition',
  isAtsRoleCreationWithoutRequisitionEnabled:
    'ATS role creation without requisition',
  isAssigneeManagedViaIntegration: 'Assignee managed via integration',
  isRoleLiveEmailEnabled: 'Role live email',
  isHiringTeamMemberAddedEmailEnabled: 'Hiring team member added email',
  isIntegrityMonitoringAvailable: 'Integrity monitoring',
  isIntegrityMonitoringAssessmentDefaultEnabled:
    'Integrity monitoring default (Assessment)',
  isIntegrityMonitoringAiInterviewDefaultEnabled:
    'Integrity monitoring default (AI Interview)',
  isAiInterviewEnabled: 'AI interview stages',
  isSpottedZebraScoreEnabled: 'Assessment score',
  isAiInterviewScoreHidingAvailable: 'Hide AI interview scores',
  isAiShortlistNationalityScrutinyEnabled: 'AI Shortlist nationality scrutiny',
  isApplicationAnswerEvidenceEnabled:
    'Application answers as criteria evidence',
  // ATS integration pipe (`setAts*` mutations)
  progressionEnabled: 'Rejection write-back',
  applicationTrackingEnabled: 'Application tracking',
  interviewEvaluationResultsEnabled: 'Interview evaluation results',
  aiInterviewResultsEnabled: 'AI Interview results',
  assessmentResultsEnabled: 'Assessment results',
  allowedRejectionReasonIds: 'Allowed rejection reasons',
  rejectOnBehalfOfUserRemoteId: 'Reject on behalf of user',
  stageMap: 'Stage mapping',
  stageRejectionReasons: 'Stage rejection reasons',
  candidatePullMode: 'Candidate pull mode',
  stagePushEnabled: 'Stage push',
  hiringTeamUserProvisioningEnabled: 'Hiring team user provisioning',
  'resultPush.AI_SHORTLIST': 'Results delivery (AI Shortlist)',
  'resultPush.AI_INTERVIEW': 'Results delivery (AI Interview)',
  'resultPush.ASSESSMENT': 'Results delivery (Assessment)',
  'resultPush.INTERVIEW_EVALUATION': 'Results delivery (Interview evaluation)',
};

export const settingLabel = (setting: string): string =>
  SETTING_LABELS[setting] ?? setting;

/**
 * Keys whose label or key contains the search text (case-insensitive). The
 * server stores keys, not labels, so this lets "Rejection write-back" find
 * `progressionEnabled`.
 */
export const settingKeysMatching = (search: string): string[] => {
  const needle = search.trim().toLowerCase();
  if (!needle) return [];
  return Object.entries(SETTING_LABELS)
    .filter(
      ([key, label]) =>
        label.toLowerCase().includes(needle) ||
        key.toLowerCase().includes(needle),
    )
    .map(([key]) => key);
};

/** Cell nouns for array-valued settings ("3 domains" beats raw JSON). */
const ARRAY_NOUNS: Record<string, [singular: string, plural: string]> = {
  allowedEmailDomains: ['domain', 'domains'],
  allowedRejectionReasonIds: ['reason', 'reasons'],
  stageMap: ['mapping', 'mappings'],
  stageRejectionReasons: ['stage', 'stages'],
};

const arraySummary = (setting: string, length: number): string => {
  const [singular, plural] = ARRAY_NOUNS[setting] ?? ['entry', 'entries'];
  return `${length} ${length === 1 ? singular : plural}`;
};

/** dd MMM yyyy, HH:mm — tolerant of epoch-millis-string serialization. */
export const formatAuditDate = (value: string): string =>
  isNaN(Number(value))
    ? format(new Date(value), 'dd MMM yyyy, HH:mm')
    : format(new Date(Number(value)), 'dd MMM yyyy, HH:mm');

/** Full value, pretty-printed for the hover tooltip. */
const prettyValue = (parsed: unknown): ReactNode => (
  <pre className={styles.tooltipJson}>{JSON.stringify(parsed, null, 2)}</pre>
);

/**
 * Renders one JSON-encoded audit value: booleans as On/Off, nulls and
 * empties as the placeholder, scalars plainly. Arrays become human
 * summaries ("3 domains") and objects a compact single line; both carry
 * the full pretty-printed value in a hover tooltip so long From/To
 * values stay distinguishable.
 */
export const formatAuditValue = (
  raw: string | null | undefined,
  setting: string,
): ReactNode => {
  if (raw === null || raw === undefined) return emptyDisplayValue;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = raw;
  }
  if (parsed === null) return emptyDisplayValue;
  if (typeof parsed === 'boolean') return parsed ? 'On' : 'Off';
  if (typeof parsed === 'string') return parsed === '' ? emptyDisplayValue : parsed;
  if (typeof parsed === 'number') return String(parsed);
  if (Array.isArray(parsed)) {
    return (
      <Tooltip content={prettyValue(parsed)}>
        <span className={styles.complexValue}>
          {arraySummary(setting, parsed.length)}
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip content={prettyValue(parsed)}>
      <span className={styles.complexValue}>{JSON.stringify(parsed)}</span>
    </Tooltip>
  );
};

type AuditRow = NonNullable<
  CompanySettingChangesQuery['companySettingChanges']['data']
>[number];

export const parseTableData = (row: AuditRow) => ({
  id: row.id,
  [CompanySettingChangeOrderField.OccurredAt]: formatAuditDate(
    String(row.occurredAt),
  ),
  [CompanySettingChangeOrderField.ChangedBy]:
    row.changedByDisplay || emptyDisplayValue,
  [CompanySettingChangeOrderField.Setting]: settingLabel(row.setting),
  oldValue: formatAuditValue(row.oldValue, row.setting),
  newValue: formatAuditValue(row.newValue, row.setting),
});
